import { useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type RowData,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  // Per-column class hooks so pages can size/truncate columns (e.g. a
  // `table-fixed` layout that fits the viewport width). Optional — columns
  // without `meta` render exactly as before.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Classes on every cell of the column — header, body and loading rows
     *  (e.g. `hidden md:table-cell` to drop the column on narrow screens). */
    className?: string;
    headerClassName?: string;
    cellClassName?: string;
  }
}
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/custom/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  searchColumn?: string;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  fillHeight?: boolean;
  defaultPageSize?: number;
  /** Server-side pagination: total row count from the API. */
  totalRows?: number;
  /** Server-side pagination: controlled pagination state. */
  pagination?: PaginationState;
  /** Server-side pagination: callback when page or pageSize changes. */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Server-side search: callback when search input changes. */
  onSearchChange?: (value: string) => void;
  /**
   * Server-side search: controlled value for the search input. Pass when the
   * search term is driven from outside (e.g. URL params) so browser
   * back/forward and shared links populate the input correctly. Omit for
   * uncontrolled use — the input then manages its own state and only emits
   * via `onSearchChange`.
   */
  searchValue?: string;
  /** Optional filter bar rendered between search input and table. */
  filterBar?: React.ReactNode;
  /** Server-side sorting: controlled sorting state. */
  sorting?: SortingState;
  /** Server-side sorting: callback when header is clicked. */
  onSortingChange?: (sorting: SortingState) => void;
  /**
   * Extra classes on the `<table>` element. Pass `table-fixed` to make
   * columns share the container width (no horizontal overflow); combine with
   * per-column `meta.headerClassName` widths.
   */
  tableClassName?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  searchPlaceholder = 'Search...',
  searchColumn,
  onRowClick,
  emptyMessage = 'No results found.',
  fillHeight = false,
  defaultPageSize = 50,
  totalRows,
  pagination: controlledPagination,
  onPaginationChange,
  onSearchChange,
  searchValue,
  filterBar,
  sorting: controlledSorting,
  onSortingChange: onControlledSortingChange,
  tableClassName,
}: DataTableProps<TData, TValue>): React.ReactElement {
  // In a `table-fixed` layout a plain-text header can't widen its column, so
  // it's clipped rather than spilling into the next one. Component headers
  // (e.g. `SortableHeader`) own their layout and are left alone.
  const isFixedLayout = tableClassName?.split(/\s+/).includes('table-fixed') ?? false;
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
  const isServerSorting = controlledSorting != null && onControlledSortingChange != null;
  const sorting = isServerSorting ? controlledSorting : uncontrolledSorting;
  const handleSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState)
  ): void => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (isServerSorting) onControlledSortingChange(next);
    else setUncontrolledSorting(next);
  };
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const isServerPagination = controlledPagination != null && onPaginationChange != null;
  const pageCount =
    isServerPagination && totalRows != null
      ? Math.ceil(totalRows / controlledPagination.pageSize)
      : undefined;

  const table = useReactTable({
    data,
    columns,
    ...(isServerPagination
      ? {
          manualPagination: true,
          pageCount,
          manualSorting: isServerSorting,
          state: {
            pagination: controlledPagination,
            sorting,
            columnFilters,
            columnVisibility,
          },
          onPaginationChange: (updater) => {
            const next = typeof updater === 'function' ? updater(controlledPagination) : updater;
            onPaginationChange(next);
          },
        }
      : {
          initialState: {
            pagination: { pageSize: defaultPageSize },
          },
          getPaginationRowModel: getPaginationRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
          state: {
            sorting,
            columnFilters,
            columnVisibility,
          },
        }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else if (searchColumn) {
      table.getColumn(searchColumn)?.setFilterValue(value);
    }
  };

  return (
    <div className={fillHeight ? 'flex min-h-0 flex-1 flex-col' : 'space-y-4'}>
      {/* Search and filter-bar are rendered inline when both are present so
          they read as one toolbar row; when only one is present we fall back
          to the old single-row layout. */}
      {(onSearchChange || searchColumn || filterBar) && (
        <div className={`flex flex-wrap items-center gap-3 ${fillHeight ? 'mb-2 shrink-0' : ''}`}>
          {onSearchChange ? (
            <SearchInput
              value={searchValue ?? ''}
              onValueChange={onSearchChange}
              placeholder={searchPlaceholder}
              className="w-full max-w-sm"
            />
          ) : searchColumn ? (
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={(table.getColumn(searchColumn)?.getFilterValue() as string) ?? ''}
                onChange={(event) => handleSearchChange(event.target.value)}
                className="pl-9"
              />
            </div>
          ) : null}
          {filterBar}
        </div>
      )}

      <div
        // `[&>[data-slot=table-container]]:overflow-visible` neutralises the
        // shadcn Table wrapper's own `overflow-x-auto`. Without this, that
        // wrapper becomes the nearest scroll ancestor and the sticky `<thead>`
        // below pins to it (full table height, never scrolls) instead of this
        // height-constrained container — so the header scrolls away. Horizontal
        // overflow still works: this `overflow-auto` container handles it.
        className={`rounded-t-md border ${fillHeight ? 'min-h-0 flex-1 overflow-auto [&>[data-slot=table-container]]:overflow-visible' : 'rounded-b-md'}`}
      >
        <Table className={tableClassName}>
          <TableHeader className={fillHeight ? 'sticky top-0 z-10 bg-background' : ''}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.columnDef.meta?.className,
                      header.column.columnDef.meta?.headerClassName
                    )}
                  >
                    {header.isPlaceholder ? null : isFixedLayout &&
                      typeof header.column.columnDef.header === 'string' ? (
                      <span className="block truncate">{header.column.columnDef.header}</span>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {table.getVisibleLeafColumns().map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(
                        column.columnDef.meta?.className,
                        column.columnDef.meta?.cellClassName
                      )}
                    >
                      <Skeleton className="h-4 w-full max-w-[150px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  // `group` lets a cell reveal a control on row hover (e.g. the
                  // trailing edit pencil on the setup list pages and the
                  // assignment-rules table).
                  className={onRowClick ? 'group cursor-pointer' : 'group'}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.columnDef.meta?.className,
                        cell.column.columnDef.meta?.cellClassName
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination footer — flush with table (no gap), always visible in fillHeight mode */}
      {(fillHeight || table.getPageCount() > 1) && (
        <div className="flex shrink-0 items-center justify-between rounded-b-md border border-t-0 bg-muted/30 px-4 py-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={String(table.getState().pagination.pageSize)}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              {/* Sized to its value (a fixed width clipped "100"); h-7 keeps the
                  footer compact. */}
              <SelectTrigger size="sm" className="gap-1 px-2 data-[size=sm]:h-7">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              className="size-7"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              className="size-7"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for sortable column headers
interface SortableHeaderProps {
  column: {
    getIsSorted: () => false | 'asc' | 'desc';
    toggleSorting: (desc?: boolean) => void;
  };
  children: React.ReactNode;
}

export function SortableHeader({ column, children }: SortableHeaderProps): React.ReactElement {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );
}
